import React, { useEffect, useState } from "react";
import {
    Card,
    CardBody,
    Row,
    Col,
    Button,
    Spinner,
    Alert,
    Input,
    Label,
    FormGroup,
    Form
} from "reactstrap";
import axios from "axios";
import { API } from "../../api/urls";
import DateTimeInput from "../../components/Common/DateTimeInput";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?\d{7,15}$/;
const MAX_VISIBLE_ONE_TIME_ROWS = 10;
const ONE_TIME_TABLE_MAX_HEIGHT = "27rem";
const REMINDER_TAB_BUTTON_RADIUS = "0.75rem";

const toDateTimeLocalInputValue = (date) => {
    const d = new Date(date);
    const pad = (value) => String(value).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
        d.getMinutes()
    )}`;
};

const getNowLocalInputValue = () => toDateTimeLocalInputValue(new Date());

const getDefaultScheduledAt = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setSeconds(0, 0);
    return toDateTimeLocalInputValue(d);
};

const getErrorMessage = (err, fallback, statusMap = {}) => {
    const status = err?.response?.status;
    return statusMap[status] || err?.response?.data?.message || fallback;
};

const AccountDebtReminderTab = ({ accountId, account, balanceSummary, formatCurrency }) => {
    const [loadingSchedule, setLoadingSchedule] = useState(false);
    const [schedule, setSchedule] = useState(null);
    const [scheduleError, setScheduleError] = useState(null);
    const [scheduleCustomEmail, setScheduleCustomEmail] = useState("");
    const [scheduleCustomPhone, setScheduleCustomPhone] = useState("");
    const [scheduleCustomEmailError, setScheduleCustomEmailError] = useState(null);
    const [scheduleCustomPhoneError, setScheduleCustomPhoneError] = useState(null);
    const [intervalDays, setIntervalDays] = useState(7);
    const [sendEmail, setSendEmail] = useState(true);
    const [sendSms, setSendSms] = useState(false);
    const [active, setActive] = useState(true);

    const [savingSchedule, setSavingSchedule] = useState(false);
    const [deletingSchedule, setDeletingSchedule] = useState(false);

    const [triggerSending, setTriggerSending] = useState(false);
    const [triggerError, setTriggerError] = useState(null);
    const [triggerSuccess, setTriggerSuccess] = useState(null);

    const [triggerSendEmail, setTriggerSendEmail] = useState(true);
    const [triggerSendSms, setTriggerSendSms] = useState(false);
    const [triggerSendWhatsapp, setTriggerSendWhatsapp] = useState(false);
    const [customEmail, setCustomEmail] = useState("");
    const [customEmailError, setCustomEmailError] = useState(null);
    const [customPhone, setCustomPhone] = useState("");
    const [customPhoneError, setCustomPhoneError] = useState(null);

    // İleri Tarihli hatırlatma state
    const [oneTimeReminders, setOneTimeReminders] = useState([]);
    const [loadingOneTime, setLoadingOneTime] = useState(false);
    const [oneTimeError, setOneTimeError] = useState(null);
    const [oneTimeSuccess, setOneTimeSuccess] = useState(null);
    const [savingOneTime, setSavingOneTime] = useState(false);
    const [cancellingOneTimeId, setCancellingOneTimeId] = useState(null);
    const [oneTimeScheduledAt, setOneTimeScheduledAt] = useState(getDefaultScheduledAt);
    const [oneTimeSendEmail, setOneTimeSendEmail] = useState(true);
    const [oneTimeSendSms, setOneTimeSendSms] = useState(false);
    const [oneTimeCustomEmail, setOneTimeCustomEmail] = useState("");
    const [oneTimeCustomPhone, setOneTimeCustomPhone] = useState("");
    const [oneTimeCustomEmailError, setOneTimeCustomEmailError] = useState(null);
    const [oneTimeCustomPhoneError, setOneTimeCustomPhoneError] = useState(null);
    const [activeReminderTab, setActiveReminderTab] = useState("trigger");

    const fmtCode = (amount, code = "TRY") =>
        new Intl.NumberFormat("tr-TR", { style: "currency", currency: code }).format(Number(amount || 0));

    // Negatif bakiyeli (borçlu) para birimleri
    const debtBalances = (balanceSummary?.balances && balanceSummary.balances.length > 0
        ? balanceSummary.balances
        : (balanceSummary?.currentBalance != null
            ? [{ currency: "TRY", currentBalance: balanceSummary.currentBalance }]
            : [])
    ).filter((b) => Number(b.currentBalance || 0) < 0);

    const hasDebt = debtBalances.length > 0;

    // Tüm borçlu para birimleri "1.000,00 ₺, 200,00 $" biçiminde
    const debtText = debtBalances
        .map((b) => fmtCode(Math.abs(Number(b.currentBalance || 0)), b.currency))
        .join(", ");

    useEffect(() => {
        if (accountId) {
            loadSchedule();
            loadOneTimeReminders();
        }
    }, [accountId]);

    const loadSchedule = async () => {
        setLoadingSchedule(true);
        setScheduleError(null);
        try {
            const res = await axios.get(API.get_debt_reminder_schedule(accountId), {
                headers: {},
            });

            const data = res.data.data;
            setSchedule(data);
            setIntervalDays(data.intervalDays ?? 7);
            setSendEmail(data.sendEmail ?? true);
            setSendSms(data.sendSms ?? false);
            setActive(data.active ?? true);
            setScheduleCustomEmail(data.customEmail ?? "");
            setScheduleCustomPhone(data.customPhoneNumber ?? "");
        } catch (err) {
            // 404 ise "plan yok", hata göstermeyelim
            if (err.response && err.response.status === 404) {
                setSchedule(null);
                setScheduleCustomEmail("");
                setScheduleCustomPhone("");
            } else {
                setScheduleError(
                    err.response?.data?.message || "Borç hatırlatma planı yüklenirken bir hata oluştu."
                );
            }
        } finally {
            setLoadingSchedule(false);
        }
    };

    const handleSaveSchedule = async (e) => {
        e.preventDefault();
        setScheduleCustomEmailError(null);
        setScheduleCustomPhoneError(null);
        setSavingSchedule(true);
        setScheduleError(null);

        const emailValue = scheduleCustomEmail.trim();
        const phoneValue = scheduleCustomPhone.trim();

        if (sendEmail && emailValue !== "" && !EMAIL_REGEX.test(emailValue)) {
            setScheduleCustomEmailError("Lütfen geçerli bir e-posta adresi girin.");
            setSavingSchedule(false);
            return;
        }

        if (sendSms && phoneValue !== "" && !PHONE_REGEX.test(phoneValue)) {
            setScheduleCustomPhoneError("Telefon formatı geçersiz. Örn: +905551112233");
            setSavingSchedule(false);
            return;
        }

        try {
            const payload = {
                accountId,
                intervalDays: Number(intervalDays),
                sendSms,
                sendEmail,
                active,
                customEmail: sendEmail ? emailValue || null : null,
                customPhoneNumber: sendSms ? phoneValue || null : null,
            };

            const res = await axios.post(API.debt_reminder_schedule_upsert, payload, {
                headers: {},
            });

            const data = res.data.data;
            setSchedule(data);
            setIntervalDays(data.intervalDays ?? payload.intervalDays);
            setSendEmail(data.sendEmail ?? sendEmail);
            setSendSms(data.sendSms ?? sendSms);
            setActive(data.active ?? active);
            setScheduleCustomEmail(data.customEmail ?? payload.customEmail ?? "");
            setScheduleCustomPhone(data.customPhoneNumber ?? payload.customPhoneNumber ?? "");
        } catch (err) {
            setScheduleError(getErrorMessage(err, "Borç hatırlatma planı kaydedilirken bir hata oluştu.", {
                400: "Gönderilen plan bilgileri geçersiz. Alanları kontrol edin.",
                404: "Cari bulunamadı veya plan ilişkisi çözümlenemedi.",
            }));
        } finally {
            setSavingSchedule(false);
        }
    };

    const handleDeleteSchedule = async () => {
        setDeletingSchedule(true);
        setScheduleError(null);
        try {
            await axios.delete(API.delete_debt_reminder_schedule(accountId), {
                headers: {},
            });
            setSchedule(null);
            setScheduleCustomEmail("");
            setScheduleCustomPhone("");
        } catch (err) {
            setScheduleError(getErrorMessage(err, "Borç hatırlatma planı silinirken bir hata oluştu.", {
                404: "Silinecek plan bulunamadı.",
            }));
        } finally {
            setDeletingSchedule(false);
        }
    };

    const handleTriggerReminder = async (e) => {
        e.preventDefault();
        setTriggerSending(true);
        setTriggerError(null);
        setTriggerSuccess(null);
        setCustomEmailError(null);
        setCustomPhoneError(null);

        const emailValue = customEmail.trim();
        const phoneValue = customPhone.trim();

        if (!triggerSendEmail && !triggerSendSms && !triggerSendWhatsapp) {
            setTriggerError("En az bir kanal seçiniz.");
            setTriggerSending(false);
            return;
        }

        if (triggerSendEmail && emailValue !== "" && !EMAIL_REGEX.test(emailValue)) {
            setCustomEmailError("Lütfen geçerli bir e-posta adresi girin.");
            setTriggerError("Lütfen geçerli bir e-posta adresi girin.");
            setTriggerSending(false);
            return;
        }

        if ((triggerSendSms || triggerSendWhatsapp) && phoneValue !== "" && !PHONE_REGEX.test(phoneValue)) {
            setCustomPhoneError("Telefon formatı geçersiz. Örn: +905551112233");
            setTriggerError("Lütfen geçerli bir telefon numarası girin.");
            setTriggerSending(false);
            return;
        }

        try {
            const payload = {
                accountId,
                sendSms: triggerSendSms,
                sendEmail: triggerSendEmail,
                sendWhatsapp: triggerSendWhatsapp,
            };

            if (triggerSendEmail && emailValue !== "") {
                payload.customEmail = emailValue;
            }

            if ((triggerSendSms || triggerSendWhatsapp) && phoneValue !== "") {
                payload.customPhoneNumber = phoneValue;
            }

            const res = await axios.post(API.debt_reminder_trigger, payload, {
                headers: {},
            });

            const dto = res.data?.data;

            // WhatsApp seçiliyse → otomatik yönlendirme
            if (triggerSendWhatsapp && dto?.whatsappNumber) {
                openWhatsAppChat(dto.whatsappNumber);
            }

            setTriggerSuccess(res.data.message || "Borç hatırlatma gönderimi başarılı.");
        } catch (err) {
            setTriggerError(getErrorMessage(err, "Borç hatırlatma gönderilirken bir hata oluştu.", {
                400: "Gönderim parametreleri geçersiz. Kanalları ve iletişim alanlarını kontrol edin.",
                404: "Cari bulunamadı.",
                429: "Kısa sürede çok fazla gönderim denediniz. Lütfen biraz sonra tekrar deneyin.",
            }));
        } finally {
            setTriggerSending(false);
        }
    };

    const loadOneTimeReminders = async () => {
        setLoadingOneTime(true);
        setOneTimeError(null);
        try {
            const res = await axios.get(API.one_time_reminder_list(accountId), {
                headers: {},
            });
            setOneTimeReminders(res.data.data || []);
        } catch (err) {
            if (!err.response || err.response.status !== 404) {
                setOneTimeError(
                    err.response?.data?.message || "İleri Tarihli hatırlatmalar yüklenirken bir hata oluştu."
                );
            }
        } finally {
            setLoadingOneTime(false);
        }
    };

    const handleCreateOneTime = async (e) => {
        e.preventDefault();
        setOneTimeCustomEmailError(null);
        setOneTimeCustomPhoneError(null);

        if (!oneTimeSendEmail && !oneTimeSendSms) {
            setOneTimeError("En az bir kanal seçiniz.");
            return;
        }

        if (isPastOneTimeDate(oneTimeScheduledAt)) {
            setOneTimeError("İleri Tarihli hatırlatma tarihi geçmişte olamaz.");
            return;
        }

        const emailValue = oneTimeCustomEmail.trim();
        const phoneValue = oneTimeCustomPhone.trim();

        if (oneTimeSendEmail && emailValue !== "" && !EMAIL_REGEX.test(emailValue)) {
            setOneTimeCustomEmailError("Lütfen geçerli bir e-posta adresi girin.");
            setOneTimeError("Lütfen geçerli bir e-posta adresi girin.");
            return;
        }

        if (oneTimeSendSms && phoneValue !== "" && !PHONE_REGEX.test(phoneValue)) {
            setOneTimeCustomPhoneError("Telefon formatı geçersiz. Örn: +905551112233");
            setOneTimeError("Lütfen geçerli bir telefon numarası girin.");
            return;
        }

        setSavingOneTime(true);
        setOneTimeError(null);
        setOneTimeSuccess(null);
        try {
            const payload = {
                accountId,
                scheduledAt: oneTimeScheduledAt + ":00",
                sendEmail: oneTimeSendEmail,
                sendSms: oneTimeSendSms,
                customEmail: oneTimeSendEmail ? emailValue || null : null,
                customPhoneNumber: oneTimeSendSms ? phoneValue || null : null,
            };
            await axios.post(API.one_time_reminder_create, payload, {
                headers: {},
            });
            setOneTimeSuccess("İleri Tarihli hatırlatma oluşturuldu.");
            setOneTimeScheduledAt(getDefaultScheduledAt());
            setOneTimeSendEmail(true);
            setOneTimeSendSms(false);
            setOneTimeCustomEmail("");
            setOneTimeCustomPhone("");
            loadOneTimeReminders();
        } catch (err) {
            setOneTimeError(getErrorMessage(err, "İleri Tarihli hatırlatma oluşturulurken bir hata oluştu.", {
                400: "İleri Tarihli plan alanları geçersiz.",
                404: "Cari bulunamadı.",
                409: "Bekleyen bir İleri Tarihli plan zaten var veya plan daha önce çalıştırılmış.",
            }));
        } finally {
            setSavingOneTime(false);
        }
    };

    const handleCancelOneTime = async (id) => {
        setCancellingOneTimeId(id);
        setOneTimeError(null);
        try {
            await axios.delete(API.one_time_reminder_cancel(id), {
                headers: {},
            });
            loadOneTimeReminders();
        } catch (err) {
            setOneTimeError(getErrorMessage(err, "Hatırlatma iptal edilirken bir hata oluştu.", {
                404: "İptal edilecek İleri Tarihli plan bulunamadı.",
                409: "Bu plan artık iptal edilemez (çalıştırılmış olabilir).",
            }));
        } finally {
            setCancellingOneTimeId(null);
        }
    };

    const getOneTimeStatusBadge = (reminder) => {
        if (reminder.executedAt) {
            return <span className="badge bg-success">Gönderildi</span>;
        }
        if (!reminder.active) {
            return <span className="badge bg-secondary">İptal Edildi</span>;
        }
        return <span className="badge bg-warning text-dark">Bekliyor</span>;
    };

    const openWhatsAppChat = (phone) => {
        if (!phone || typeof window === "undefined") return;
        const cleaned = phone.toString().replace(/\D/g, "");
        const amountText = debtText || fmtCode(0, "TRY");
        const accountName = account?.accountName || "müşterimiz";
        const message = `Merhaba ${accountName}, ${amountText} tutarında cari borcunuz bulunmaktadır. Tahsilat360 üzerinden size borç hatırlatma iletisi gönderilmiştir.`;
        const url = `https://api.whatsapp.com/send?phone=${cleaned}&text=${encodeURIComponent(
            message
        )}`;
        window.open(url, "_blank");
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleString("tr-TR", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const isPastOneTimeDate = (value) => {
        if (!value) return true;
        const selectedDate = new Date(value);
        if (Number.isNaN(selectedDate.getTime())) return true;
        return selectedDate.getTime() < Date.now();
    };

    return (
        <Card className="bg-white border-0 shadow-lg rounded-4 mt-2">
            <CardBody className="p-4">
                <Row className="mb-3">
                    <Col>
                        <h5 className="mb-1 fw-semibold">
                            <i className="bx bx-bell me-2" />
                            Borç Hatırlatma
                        </h5>
                        <p className="text-muted small mb-0">
                            {account?.accountName} için anlık, periyodik veya İleri Tarihli borç hatırlatma yönetebilirsiniz.
                        </p>
                    </Col>
                </Row>

                {hasDebt ? (
                    <Alert color="danger" className="py-2">
                        Bu carinin borç bakiyesi bulunmaktadır. ({debtText})
                    </Alert>
                ) : (
                    <Alert color="info" className="py-2">
                        Bu carinin şu anda borç bakiyesi bulunmamaktadır.
                    </Alert>
                )}
                <div className="border rounded-4 p-2 mb-3" style={{ backgroundColor: "#f3f4f6" }}>
                    <div className="d-flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            color={activeReminderTab === "trigger" ? "primary" : "light"}
                            className="px-3"
                            style={{ borderRadius: REMINDER_TAB_BUTTON_RADIUS }}
                            onClick={() => setActiveReminderTab("trigger")}
                        >
                            Anlık Gönderim
                        </Button>
                        <Button
                            size="sm"
                            color={activeReminderTab === "schedule" ? "primary" : "light"}
                            className="px-3"
                            style={{ borderRadius: REMINDER_TAB_BUTTON_RADIUS }}
                            onClick={() => setActiveReminderTab("schedule")}
                        >
                            Periyodik Plan
                        </Button>
                        <Button
                            size="sm"
                            color={activeReminderTab === "one-time" ? "primary" : "light"}
                            className="px-3"
                            style={{ borderRadius: REMINDER_TAB_BUTTON_RADIUS }}
                            onClick={() => setActiveReminderTab("one-time")}
                        >
                            İleri Tarihli Plan
                        </Button>
                    </div>
                </div>

                {activeReminderTab === "trigger" && (
                    <Row>
                        <Col>
                            <div className="border rounded-4 p-3 mb-3" style={{ backgroundColor: "#f9fafb" }}>
                            <h6 className="fw-semibold mb-2">Anlık Borç Hatırlatma</h6>
                            <p className="text-muted small mb-3">
                                Seçtiğiniz kanallar üzerinden bu cariye hemen borç hatırlatma iletisi gönderebilirsiniz.
                            </p>

                            {triggerError && (
                                <Alert color="danger" className="py-2">
                                    {triggerError}
                                </Alert>
                            )}
                            {triggerSuccess && (
                                <Alert color="success" className="py-2">
                                    {triggerSuccess}
                                </Alert>
                            )}

                            <Form onSubmit={handleTriggerReminder}>
                                <FormGroup className="mb-2">
                                    <div className="alert alert-light border p-2 small mt-2" style={{ borderRadius: "10px" }}>
                                        <i className="bx bx-info-circle me-1"></i>
                                        <strong>Not:</strong> Boş bırakılırsa sistemdeki cari admin iletişim bilgileri kullanılır
                                        (admin bilgisi yoksa cari telefonuna fallback edilir).
                                    </div>
                                    <Label className="small fw-semibold mb-1">Kanallar</Label>
                                    <div className="d-flex flex-wrap gap-3">
                                        <div className="form-check">
                                            <Input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="triggerEmail"
                                                checked={triggerSendEmail}
                                                onChange={(e) => setTriggerSendEmail(e.target.checked)}
                                            />
                                            <Label className="form-check-label small" for="triggerEmail">
                                                E-posta
                                            </Label>
                                        </div>
                                        <div className="form-check">
                                            <Input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="triggerSms"
                                                checked={triggerSendSms}
                                                onChange={(e) => setTriggerSendSms(e.target.checked)}
                                            />
                                            <Label className="form-check-label small" for="triggerSms">
                                                SMS
                                            </Label>
                                        </div>
                                        <div className="form-check">
                                            <Input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="triggerWhatsapp"
                                                checked={triggerSendWhatsapp}
                                                onChange={(e) => setTriggerSendWhatsapp(e.target.checked)}
                                            />
                                            <Label className="form-check-label small" for="triggerWhatsapp">
                                                WhatsApp (numara döndürülür)
                                            </Label>
                                        </div>
                                    </div>
                                </FormGroup>

                                <FormGroup className="mb-2">
                                    <Label className="small mb-1">Manuel E-posta (opsiyonel)</Label>
                                    <Input
                                        type="email"
                                        value={customEmail}
                                        onChange={(e) => {
                                            setCustomEmail(e.target.value);
                                            if (customEmailError) setCustomEmailError(null);
                                        }}
                                        placeholder="Örn: cari@mailadresiniz.com"
                                        bsSize="sm"
                                        disabled={!triggerSendEmail}
                                        invalid={!!customEmailError}
                                    />
                                    {customEmailError && (
                                        <div className="invalid-feedback d-block">
                                            {customEmailError}
                                        </div>
                                    )}
                                </FormGroup>

                                <FormGroup className="mb-3">
                                    <Label className="small mb-1">Manuel Telefon (opsiyonel)</Label>
                                    <Input
                                        type="text"
                                        value={customPhone}
                                        onChange={(e) => setCustomPhone(e.target.value)}
                                        placeholder="Örn: +905551112233"
                                        bsSize="sm"
                                        disabled={!(triggerSendSms || triggerSendWhatsapp)}
                                        invalid={!!customPhoneError}
                                    />
                                    {customPhoneError && (
                                        <div className="invalid-feedback d-block">
                                            {customPhoneError}
                                        </div>
                                    )}
                                </FormGroup>

                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    {hasDebt && (
                                        <small className="text-muted">
                                            Güncel borç bakiyesi:{" "}
                                            <span className="fw-semibold text-danger">
                                              {debtText}
                                            </span>
                                        </small>
                                    )}
                                </div>
                                        <div className="d-flex justify-content-end">
                                            <Button
                                                color="primary"
                                                size="sm"
                                        type="submit"
                                        disabled={triggerSending || !hasDebt}
                                        className="rounded-pill px-3"
                                    >
                                        {triggerSending ? (
                                            <>
                                                <Spinner size="sm" className="me-1" /> Gönderiliyor...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bx bx-send me-1" />
                                                Hatırlatma Gönder
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </div>
                        </Col>
                    </Row>
                )}

                {activeReminderTab === "schedule" && (
                    <Row>
                        <Col>
                            <div className="border rounded-4 p-3 mb-3" style={{ backgroundColor: "#f9fafb" }}>
                            <h6 className="fw-semibold mb-2">Planlı Borç Hatırlatma</h6>
                            <p className="text-muted small mb-3">
                                Bu cariye belirli aralıklarla otomatik borç hatırlatma gönderebilirsiniz.
                            </p>

                            {loadingSchedule && (
                                <div className="text-center py-3">
                                    <Spinner size="sm" className="me-2" />
                                    Plan bilgileri yükleniyor...
                                </div>
                            )}

                            {scheduleError && (
                                <Alert color="danger" className="py-2">
                                    {scheduleError}
                                </Alert>
                            )}

                            {!loadingSchedule && (
                                <>
                                    {schedule && (
                                        <div className="mb-3 small">
                                            <p className="mb-1">
                                                <strong>Durum:</strong>{" "}
                                                {schedule.active ? (
                                                    <span className="text-success">Aktif</span>
                                                ) : (
                                                    <span className="text-muted">Pasif</span>
                                                )}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Periyot:</strong> {schedule.intervalDays} günde bir
                                            </p>
                                            <p className="mb-1">
                                                <strong>Kanallar:</strong>{" "}
                                                {[
                                                    schedule.sendEmail ? "E-posta" : null,
                                                    schedule.sendSms ? "SMS" : null,
                                                ]
                                                    .filter(Boolean)
                                                    .join(" + ") || "-"}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Son Çalışma:</strong>{" "}
                                                {formatDateTime(schedule.lastRunAt)}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Sonraki Çalışma:</strong>{" "}
                                                {formatDateTime(schedule.nextRunAt)}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Manuel E-posta:</strong> {schedule.customEmail || "-"}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Manuel Telefon:</strong> {schedule.customPhoneNumber || "-"}
                                            </p>
                                        </div>
                                    )}

                                    <Form onSubmit={handleSaveSchedule}>
                                        <div className="alert alert-light border p-2 small mt-1" style={{ borderRadius: "10px" }}>
                                            <i className="bx bx-info-circle me-1"></i>
                                            Boş bırakılırsa sistemdeki cari admin bilgisi kullanılır.
                                        </div>
                                        <FormGroup className="mb-2">
                                            <Label className="small mb-1">Hatırlatma Periyodu</Label>
                                            <Input
                                                type="select"
                                                bsSize="sm"
                                                value={intervalDays}
                                                onChange={(e) => setIntervalDays(e.target.value)}
                                            >
                                                <option value={7}>Her 1 Hafta</option>
                                                <option value={14}>Her 2 Hafta</option>
                                                <option value={30}>Her 1 Ay</option>
                                                <option value={60}>Her 2 Ay</option>
                                                <option value={90}>Her 3 Ay</option>
                                            </Input>
                                        </FormGroup>

                                        <FormGroup className="mb-2">
                                            <Label className="small mb-1">Kanallar</Label>
                                            <div className="d-flex flex-wrap gap-3">
                                                <div className="form-check">
                                                    <Input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id="scheduleEmail"
                                                        checked={sendEmail}
                                                        onChange={(e) => setSendEmail(e.target.checked)}
                                                    />
                                                    <Label
                                                        className="form-check-label small"
                                                        for="scheduleEmail"
                                                    >
                                                        E-posta
                                                    </Label>
                                                </div>
                                                <div className="form-check">
                                                    <Input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id="scheduleSms"
                                                        checked={sendSms}
                                                        onChange={(e) => setSendSms(e.target.checked)}
                                                    />
                                                    <Label
                                                        className="form-check-label small"
                                                        for="scheduleSms"
                                                    >
                                                        SMS
                                                    </Label>
                                                </div>
                                            </div>
                                        </FormGroup>

                                        <FormGroup className="mb-2">
                                            <Label className="small mb-1">Manuel E-posta (opsiyonel)</Label>
                                            <Input
                                                type="email"
                                                bsSize="sm"
                                                value={scheduleCustomEmail}
                                                onChange={(e) => {
                                                    setScheduleCustomEmail(e.target.value);
                                                    if (scheduleCustomEmailError) setScheduleCustomEmailError(null);
                                                }}
                                                disabled={!sendEmail}
                                                placeholder="Örn: tahsilat@firma.com"
                                                invalid={!!scheduleCustomEmailError}
                                            />
                                            {scheduleCustomEmailError && (
                                                <div className="invalid-feedback d-block">{scheduleCustomEmailError}</div>
                                            )}
                                        </FormGroup>

                                        <FormGroup className="mb-2">
                                            <Label className="small mb-1">Manuel Telefon (opsiyonel)</Label>
                                            <Input
                                                type="text"
                                                bsSize="sm"
                                                value={scheduleCustomPhone}
                                                onChange={(e) => {
                                                    setScheduleCustomPhone(e.target.value);
                                                    if (scheduleCustomPhoneError) setScheduleCustomPhoneError(null);
                                                }}
                                                disabled={!sendSms}
                                                placeholder="Örn: +905551112233"
                                                invalid={!!scheduleCustomPhoneError}
                                            />
                                            {scheduleCustomPhoneError && (
                                                <div className="invalid-feedback d-block">{scheduleCustomPhoneError}</div>
                                            )}
                                        </FormGroup>

                                        <FormGroup className="mb-3">
                                            <div className="form-check form-switch">
                                                <Input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="scheduleActive"
                                                    checked={active}
                                                    onChange={(e) => setActive(e.target.checked)}
                                                />
                                                <Label
                                                    className="form-check-label small"
                                                    for="scheduleActive"
                                                >
                                                    Plan aktif olsun
                                                </Label>
                                            </div>
                                        </FormGroup>

                                        <div className="d-flex justify-content-end align-items-center gap-2">
                                            {schedule && (
                                                <Button
                                                    color="outline-danger"
                                                    size="sm"
                                                    type="button"
                                                    disabled={deletingSchedule}
                                                    onClick={handleDeleteSchedule}
                                                    className="rounded-pill px-3"
                                                >
                                                    {deletingSchedule ? (
                                                        <>
                                                            <Spinner size="sm" className="me-1" /> İşleniyor...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bx bx-trash me-1" />
                                                            Planı Kaldır
                                                        </>
                                                    )}
                                                </Button>
                                            )}
                                            <Button
                                                color="primary"
                                                size="sm"
                                                type="submit"
                                                disabled={savingSchedule}
                                                className="rounded-pill px-3"
                                            >
                                                {savingSchedule ? (
                                                    <>
                                                        <Spinner size="sm" className="me-1" /> Kaydediliyor...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bx bx-save me-1" />
                                                        Planı Kaydet
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </Form>
                                </>
                            )}
                        </div>
                        </Col>
                    </Row>
                )}

                {activeReminderTab === "one-time" && (
                    <Row>
                        <Col>
                        <div className="border rounded-4 p-3" style={{ backgroundColor: "#f9fafb" }}>
                            <h6 className="fw-semibold mb-1">
                                <i className="bx bx-calendar-event me-2" />
                                İleri Tarihli Hatırlatma
                            </h6>
                            <p className="text-muted small mb-3">
                                Belirlediğiniz tarih ve saatte bu cariye bir kez otomatik borç hatırlatma gönderilir.
                            </p>

                            <Row>
                                <Col md={4}>
                                    {oneTimeError && (
                                        <Alert color="danger" className="py-2">
                                            {oneTimeError}
                                        </Alert>
                                    )}
                                    {oneTimeSuccess && (
                                        <Alert color="success" className="py-2">
                                            {oneTimeSuccess}
                                        </Alert>
                                    )}

                                    <Form id="oneTimeReminderForm" onSubmit={handleCreateOneTime}>
                                        <div className="alert alert-light border p-2 small mt-1" style={{ borderRadius: "10px" }}>
                                            <i className="bx bx-info-circle me-1"></i>
                                            Boş bırakılırsa sistemdeki cari admin bilgisi kullanılır.
                                        </div>
                                        <FormGroup className="mb-2">
                                            <Label className="small fw-semibold mb-1">Tarih ve Saat</Label>
                                            <DateTimeInput
                                                className="form-control-sm"
                                                value={oneTimeScheduledAt}
                                                minDate={getNowLocalInputValue()}
                                                onChange={(nextValue) => {
                                                    setOneTimeScheduledAt(nextValue);
                                                    if (oneTimeError && !isPastOneTimeDate(nextValue)) {
                                                        setOneTimeError(null);
                                                    }
                                                }}
                                                required
                                            />
                                        </FormGroup>

                                        <FormGroup className="mb-3">
                                            <Label className="small fw-semibold mb-1">Kanallar</Label>
                                            <div className="d-flex flex-wrap gap-3">
                                                <div className="form-check">
                                                    <Input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id="oneTimeEmail"
                                                        checked={oneTimeSendEmail}
                                                        onChange={(e) => setOneTimeSendEmail(e.target.checked)}
                                                    />
                                                    <Label className="form-check-label small" for="oneTimeEmail">
                                                        E-posta
                                                    </Label>
                                                </div>
                                                <div className="form-check">
                                                    <Input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id="oneTimeSms"
                                                        checked={oneTimeSendSms}
                                                        onChange={(e) => setOneTimeSendSms(e.target.checked)}
                                                    />
                                                    <Label className="form-check-label small" for="oneTimeSms">
                                                        SMS
                                                    </Label>
                                                </div>
                                            </div>
                                        </FormGroup>

                                        <FormGroup className="mb-2">
                                            <Label className="small mb-1">Manuel E-posta (opsiyonel)</Label>
                                            <Input
                                                type="email"
                                                bsSize="sm"
                                                value={oneTimeCustomEmail}
                                                onChange={(e) => {
                                                    setOneTimeCustomEmail(e.target.value);
                                                    if (oneTimeCustomEmailError) setOneTimeCustomEmailError(null);
                                                }}
                                                disabled={!oneTimeSendEmail}
                                                placeholder="Örn: tahsilat@firma.com"
                                                invalid={!!oneTimeCustomEmailError}
                                            />
                                            {oneTimeCustomEmailError && (
                                                <div className="invalid-feedback d-block">{oneTimeCustomEmailError}</div>
                                            )}
                                        </FormGroup>

                                        <FormGroup className="mb-3">
                                            <Label className="small mb-1">Manuel Telefon (opsiyonel)</Label>
                                            <Input
                                                type="text"
                                                bsSize="sm"
                                                value={oneTimeCustomPhone}
                                                onChange={(e) => {
                                                    setOneTimeCustomPhone(e.target.value);
                                                    if (oneTimeCustomPhoneError) setOneTimeCustomPhoneError(null);
                                                }}
                                                disabled={!oneTimeSendSms}
                                                placeholder="Örn: +905551112233"
                                                invalid={!!oneTimeCustomPhoneError}
                                            />
                                            {oneTimeCustomPhoneError && (
                                                <div className="invalid-feedback d-block">{oneTimeCustomPhoneError}</div>
                                            )}
                                        </FormGroup>

                                    </Form>
                                </Col>

                                <Col md={8}>
                                    {loadingOneTime ? (
                                        <div className="text-center py-3">
                                            <Spinner size="sm" className="me-2" />
                                            Yükleniyor...
                                        </div>
                                    ) : oneTimeReminders.length === 0 ? (
                                        <div className="text-muted small text-center py-4 border rounded-4 bg-white">
                                            Henüz İleri Tarihli hatırlatma eklenmemiş.
                                        </div>
                                    ) : (
                                        <div
                                            className="table-responsive border rounded-4 overflow-hidden shadow-sm bg-white"
                                            style={{ maxHeight: ONE_TIME_TABLE_MAX_HEIGHT, overflowY: "auto" }}
                                        >
                                            <table className="table table-sm table-hover align-middle small mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Tarih / Saat</th>
                                                        <th>Kanallar</th>
                                                        <th>Çalıştırıldı</th>
                                                        <th>Aktif</th>
                                                        <th>Durum</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {oneTimeReminders.slice(0, MAX_VISIBLE_ONE_TIME_ROWS).map((r) => (
                                                        <tr key={r.id}>
                                                            <td>{formatDateTime(r.scheduledAt)}</td>
                                                            <td>
                                                                {[
                                                                    r.sendEmail ? "E-posta" : null,
                                                                    r.sendSms ? "SMS" : null,
                                                                ]
                                                                    .filter(Boolean)
                                                                    .join(" + ") || "-"}
                                                            </td>
                                                            <td>{formatDateTime(r.executedAt)}</td>
                                                            <td>
                                                                {r.active ? (
                                                                    <span className="badge bg-success-subtle text-success">Aktif</span>
                                                                ) : (
                                                                    <span className="badge bg-secondary">Pasif</span>
                                                                )}
                                                            </td>
                                                            <td>{getOneTimeStatusBadge(r)}</td>
                                                            <td>
                                                                {r.active && !r.executedAt && (
                                                                    <Button
                                                                        color="outline-danger"
                                                                        size="sm"
                                                                        className="rounded-pill px-2 py-0"
                                                                        disabled={cancellingOneTimeId === r.id}
                                                                        onClick={() => handleCancelOneTime(r.id)}
                                                                    >
                                                                        {cancellingOneTimeId === r.id ? (
                                                                            <Spinner size="sm" />
                                                                        ) : (
                                                                            <i className="bx bx-x" />
                                                                        )}
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                            <div className="d-flex justify-content-start mt-3">
                                <Button
                                    color="primary"
                                    size="sm"
                                    type="submit"
                                    form="oneTimeReminderForm"
                                    disabled={savingOneTime || !hasDebt}
                                    className="rounded-pill px-3"
                                >
                                    {savingOneTime ? (
                                        <>
                                            <Spinner size="sm" className="me-1" /> Oluşturuluyor...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bx bx-plus me-1" />
                                            Hatırlatma Ekle
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                        </Col>
                    </Row>
                )}
            </CardBody>
        </Card>
    );
};

export default AccountDebtReminderTab;
